(function (root, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  root.DrawerCore = api;
})(typeof window !== 'undefined' ? window : globalThis, function () {
  const CM_PER_IN = 2.54;
  const TRAYS = Object.freeze([
    { sku: 'DCH1042A', width: 10, depth: 42, height: 6.8, style: 'A' },
    { sku: 'DCH1047A', width: 10, depth: 47, height: 6.8, style: 'A' },
    { sku: 'DCH1542A', width: 15, depth: 42, height: 6.8, style: 'A' },
    { sku: 'DCH1547A', width: 15, depth: 47, height: 6.8, style: 'A' },
    { sku: 'DCH1542B', width: 15, depth: 42, height: 6.8, style: 'B' },
    { sku: 'DCH1547B', width: 15, depth: 47, height: 6.8, style: 'B' }
  ]);
  const SIZES = Object.freeze([
    { id: 'compact', name: 'Compact', width: 18, depth: 18, height: 3 },
    { id: 'classic', name: 'Classic', width: 22, depth: 19, height: 3 },
    { id: 'grand', name: 'Grand', width: 28, depth: 19, height: 3 }
  ]);
  function tray(key) {
    if (typeof key !== 'string') return undefined;
    const rotated = key.endsWith(':R'), sku = rotated ? key.slice(0, -2) : key;
    const item = TRAYS.find(item => item.sku === sku);
    if (!item) return undefined;
    return {...item, sku:key, baseSku:sku, rotated, compartments:item.style==='B'?4:3, productWidth:item.width, productDepth:item.depth,
      width:rotated ? item.depth : item.width, depth:rotated ? item.width : item.depth};
  }
  function rotateKey(key) { const item=tray(key); return item ? item.baseSku + (item.rotated ? '' : ':R') : undefined; }
  function options() { return TRAYS.flatMap(item=>[tray(item.sku),tray(item.sku+':R')]); }
  function quantities(keys) { const counts=new Map();keys.forEach(key=>{const item=tray(key);if(!item)throw new Error('Unknown tray.');counts.set(item.baseSku,(counts.get(item.baseSku)||0)+1);});return counts; }
  function insertOptions(key) {const current=tray(key);return current?TRAYS.filter(item=>item.width===current.productWidth&&item.depth===current.productDepth).map(item=>tray(item.sku+(current.rotated?':R':''))):[];}
  function size(id) {
    if (id && typeof id === 'object') {
      const { width, depth, height } = id;
      if (![width, depth, height].every(value => typeof value === 'number' && Number.isFinite(value) && value > 0 && value <= 100)) return undefined;
      return { id: 'custom', name: 'Measured', width, depth, height };
    }
    return SIZES.find(item => item.id === id);
  }
  function measuredSize(values, unit) {
    if (!['in', 'cm'].includes(unit)) return undefined;
    if (!['width', 'depth', 'height'].every(key => String(values[key] ?? '').trim() !== '')) return undefined;
    const dimensions = Object.fromEntries(['width', 'depth', 'height'].map(key => [key, unit === 'cm' ? inches(Number(values[key])) : Number(values[key])]));
    return size(dimensions);
  }
  function cm(inches) { return inches * CM_PER_IN; }
  function inches(cmValue) { return cmValue / CM_PER_IN; }
  function quote(quantity, unitPrice = 2800, regularPrice = 3400) {
    if (!Number.isInteger(quantity) || quantity < 0 || !Number.isSafeInteger(unitPrice) || unitPrice < 0) throw new Error('Invalid tray quantity or price.');
    const subtotal = quantity * unitPrice;
    const discount = quantity >= 4 ? Math.round(subtotal * 0.1) : 0;
    return { quantity, subtotal, discount, total: subtotal - discount, regularTotal: quantity * regularPrice, unitPrice, bundle: quantity >= 4 };
  }
  function fit(sizeId, skus, { previewHeight = false } = {}) {
    const drawer = size(sizeId);
    if (!drawer) return { valid: false, reason: 'Choose a drawer size.' };
    const items = skus.map(tray);
    if (items.some(item => !item)) return { valid: false, reason: 'A tray is unavailable.' };
    const width = cm(drawer.width), depth = cm(drawer.depth);
    const placements = pack(width, depth, items);
    const widthUsed = placements ? Math.max(0,...placements.map(item=>item.x+item.width)) : items.reduce((sum,item)=>sum+item.width,0);
    const depthUsed = placements ? Math.max(0,...placements.map(item=>item.y+item.depth)) : 0;
    const tooDeep = items.some(item => item.depth > cm(drawer.depth) + 0.001);
    const tooTall = items.some(item => item.height > cm(drawer.height) + 0.001);
    const widthLeft = cm(drawer.width) - widthUsed;
    const footprintValid = placements !== null;
    return {
      valid: footprintValid && (previewHeight || !tooTall),
      footprintValid, heightValid: !tooTall,
      heightShortfall: Math.max(0, ...items.map(item => item.height - cm(drawer.height))),
      reason: tooDeep ? 'A tray is deeper than this drawer in this orientation.' : !footprintValid ? 'These trays cannot fit without overlap in this orientation.' : tooTall ? 'A tray is taller than this drawer.' : '',
      widthUsed, widthLeft: Math.max(0, widthLeft), depthUsed, depthLeft:Math.max(0,depth-depthUsed), placements:placements||[], drawer, items,
      coverage:items.reduce((sum,item)=>sum+item.width*item.depth,0)/(width*depth)
    };
  }
  // Fixed-orientation rectangle packing. Every accepted placement is within bounds and non-overlapping.
  // Try the current order first to preserve existing layouts; bounded fallbacks can recover fragmented space.
  function pack(width, depth, items) {
    const indexed=items.map((item,index)=>({...item,index}));
    const orders=[indexed,[...indexed].sort((a,b)=>b.width*b.depth-a.width*a.depth),[...indexed].sort((a,b)=>b.depth-a.depth||b.width-a.width)];
    for(const order of orders){
      let free=[{x:0,y:0,width,depth}], placed=[], failed=false;
      for(const item of order){
        const spaces=free.filter(r=>item.width<=r.width+.001&&item.depth<=r.depth+.001).sort((a,b)=>a.y-b.y||a.x-b.x||a.width*a.depth-b.width*b.depth);
        if(!spaces.length){failed=true;break;}
        const spot=spaces[0],p={...item,x:spot.x,y:spot.y}; placed.push(p);
        const next=[];
        for(const r of free){
          const right=r.x+r.width,bottom=r.y+r.depth,px=p.x+p.width,py=p.y+p.depth;
          if(p.x>=right-.001||px<=r.x+.001||p.y>=bottom-.001||py<=r.y+.001){next.push(r);continue;}
          if(p.x>r.x+.001)next.push({...r,width:p.x-r.x});
          if(px<right-.001)next.push({...r,x:px,width:right-px});
          if(p.y>r.y+.001)next.push({...r,depth:p.y-r.y});
          if(py<bottom-.001)next.push({...r,y:py,depth:bottom-py});
        }
        free=next.filter((r,i)=>r.width>.001&&r.depth>.001&&!next.some((s,j)=>j!==i&&s.x<=r.x+.001&&s.y<=r.y+.001&&s.x+s.width>=r.x+r.width-.001&&s.y+s.depth>=r.y+r.depth-.001&&(j<i||s.width*s.depth>r.width*r.depth+.001)));
      }
      if(!failed)return placed.sort((a,b)=>a.index-b.index);
    }
    return null;
  }
  // Planning can retain a height warning; checkout always uses strict fit().
  function layoutFit(sizeId, skus) { return fit(sizeId, skus, { previewHeight: true }); }
  function cleanedDesign(raw) {
    const sizeId = typeof raw?.sizeId === 'object' ? size(raw.sizeId) : typeof raw?.sizeId === 'string' ? raw.sizeId : 'classic';
    const skus = Array.isArray(raw?.skus) ? raw.skus.slice(0, 12).filter(sku => typeof sku === 'string' && tray(sku)) : [];
    if (!size(sizeId) || !layoutFit(sizeId, skus).valid) return { sizeId: 'classic', skus: [] };
    return { sizeId, skus };
  }
  function replaceTray(sizeId, skus, index, sku, options) {
    if (!Number.isInteger(index) || index < 0 || index >= skus.length || !tray(sku)) {
      return { valid: false, reason: 'Choose a tray to change.', skus: [...skus] };
    }
    const next = skus.map((current, i) => i === index ? sku : current);
    const result = fit(sizeId, next, options);
    return { valid: result.valid, reason: result.reason, skus: result.valid ? next : [...skus] };
  }
  // Propose a resize without changing the shopper's existing design.
  function resizeDesign(sizeId, skus, options) {
    if (!size(sizeId)) return { valid: false, skus: [...skus], removed: [] };
    const kept = [], removed = [];
    skus.forEach((sku, index) => {
      if (fit(sizeId, [...kept, sku], options).valid) kept.push(sku);
      else removed.push({ sku, index });
    });
    return { valid: true, sizeId, skus: kept, removed };
  }
  // Maximize usable width, then prefer fewer modules. Suggestions are editable.
  function suggestLayout(sizeId) {
    const drawer = size(sizeId);
    if (!drawer) return [];
    const depths = [47, 42].filter(depth => depth <= cm(drawer.depth) + .001);
    let best = { used: 0, skus: [] };
    for (let wide = 0; depths.length && wide <= 12; wide++) for (let slim = 0; slim + wide <= 12; slim++) {
      const used = wide * 15 + slim * 10, count = wide + slim;
      if (!count || used > cm(drawer.width) + .001) continue;
      if (used > best.used || (used === best.used && count < best.skus.length)) {
        best = { used, skus: [...Array(wide)].map((_, i) => 'DCH15' + depths[0] + (i % 2 ? 'B' : 'A')).concat(Array(slim).fill('DCH10' + depths[0] + 'A')) };
      }
    }
    // A deeper drawer can use horizontal modules behind the front row.
    let result=best.skus;
    const candidates=options().filter(item=>layoutFit(sizeId,[item.sku]).valid).sort((a,b)=>b.width*b.depth-a.width*a.depth);
    // Also try a horizontal-first seed when a vertical tray cannot fit at all.
    const seeds=[result, ...candidates.filter(item=>item.rotated).map(item=>[item.sku])];
    let bestArea=-1;
    for(const seed of seeds){
      let current=[...seed];
      while(current.length<12){
        const next=candidates.find(item=>layoutFit(sizeId,[...current,item.sku]).valid);
        if(!next)break;
        current.push(next.sku);
      }
      const area=current.reduce((sum,key)=>{const item=tray(key);return sum+item.width*item.depth;},0);
      if(area>bestArea||(area===bestArea&&current.length<result.length)){result=current;bestArea=area;}
    }
    return result;
  }
  return { TRAYS, SIZES, tray, rotateKey, options, quantities, insertOptions, size, measuredSize, cm, inches, quote, fit, layoutFit, cleanedDesign, replaceTray, resizeDesign, suggestLayout };
});
